/**
 * Phone number validation and formatting utilities
 */

/**
 * Validates if a phone number is a valid US or Pakistan phone number
 * @param {string} phoneNumber - The phone number to validate
 * @returns {boolean} - True if valid US or PK phone number
 */
export const isValidUSPhoneNumber = phoneNumber => {
  if (!phoneNumber || typeof phoneNumber !== 'string') {
    return false;
  }

  // Remove all non-digit characters
  const cleaned = phoneNumber.replace(/\D/g, '');

  // US phone numbers: 10 digits (without country code) or 11 digits (with country code 1)
  if (cleaned.length === 10) {
    return true;
  } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return true;
  }
  // Pakistan phone numbers: 12 digits (with country code 92)
  else if (cleaned.length === 12 && cleaned.startsWith('92')) {
    return true;
  }

  return false;
};

/**
 * Formats a phone number for display
 * @param {string} phoneNumber - The phone number to format
 * @returns {string} - Formatted phone number (e.g., "(555) 123-4567")
 */
export const formatPhoneNumber = phoneNumber => {
  if (!phoneNumber || typeof phoneNumber !== 'string') {
    return '';
  }

  // Remove all non-digit characters
  const cleaned = phoneNumber.replace(/\D/g, '');

  // Handle different lengths
  if (cleaned.length === 10) {
    // Format as (XXX) XXX-XXXX
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
    // Format as +1 (XXX) XXX-XXXX
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }

  // Return original if can't format
  return phoneNumber;
};

/**
 * Normalizes a phone number for API calls (removes formatting, ensures proper format)
 * @param {string} phoneNumber - The phone number to normalize
 * @returns {string} - Normalized phone number for API calls
 */
export const normalizePhoneNumber = phoneNumber => {
  if (!phoneNumber || typeof phoneNumber !== 'string') {
    return '';
  }

  // Remove all non-digit characters
  const cleaned = phoneNumber.replace(/\D/g, '');

  // If 10 digits, add country code
  if (cleaned.length === 10) {
    return `+1${cleaned}`;
  }

  // If 11 digits starting with 1, add +
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+${cleaned}`;
  }

  // Return as-is if already formatted
  return phoneNumber;
};
